<template>
  <div :class="{'app-header__fixed' : !unstickHeader}" class="app-header">
    <div class="left">
      <!-- LOGO (OR MOBILE BACK ARROW) -->
      <template v-if="$screen.isMobile($mq)">
        <i
          v-if="!hideMobileBackButton"
          class="ddi ddi-caret-left mobile-back-button"
          @click="$router.go(-1)"
        />
      </template>
      <router-link v-else-if="showFullOptions" :to="RouteAssistant.getDashboardRoute($route.params.accountId)" >
        <img v-bind="logo" :width="logoSize" :height="logoSize">
      </router-link>
      <!-- NAVIGATION -->
      <div v-if="showNav" class="nav">
        <!-- Main items (Home, Tasks, Convos) -->
        <ul>
          <li :class="{'selected': isCurrentRoute(routerNames.DASHBOARD)}">
            <router-link :to="{ name: routerNames.DASHBOARD }" title="Home"><span>Home</span></router-link>
          </li>
          <li :class="{'selected': isCurrentRoute(routerNames.ALL_TASKS)}">
            <router-link :to="{ name: routerNames.ALL_TASKS }" title="Tasks"><span>Tasks</span></router-link>
          </li>
          <template v-if="$permissions.isNotGuest">
            <li :class="{'selected': isCurrentRoute(routerNames.ALL_CONVERSATIONS)}">
              <router-link :to="{ name: routerNames.ALL_CONVERSATIONS }" title="Conversations"><span>Conversations</span></router-link>
            </li>
          </template>
          <template v-if="$permissions.isAdminOrOwner">
            <li :class="{'selected': isWorkflowsRoute()}">
              <router-link :to="{ name: routerNames.WORKFLOW_HOME }" title="Workflows"><span>Workflows</span></router-link>
            </li>  
            <li :class="{'selected': isPeopleRoute()}">
              <router-link :to="{ name: routerNames.PEOPLE }" title="Users"><span>Users</span></router-link>
            </li>
            <template v-if="$permissions.isTieredPricingPlan">
              <li :class="{'selected': isGuestRoute()}">
                <router-link :to="{ name: routerNames.GUESTS }" title="Guests"><span>Guests</span></router-link>
              </li>
            </template>
          </template>
        </ul>
        <!-- MORE OPTIONS (Calendar, Reports, Workflows) -->
        <more-button-dropdown :offset="[0, 22]" class="more-nav">
          <template #options-list>
            <ul>
              <li @click="RouteAssistant.goToCalendar($router, $route.params.accountId)">
                <i class="ddi ddi-calendar" /><span>Calendar</span>
              </li>
              <li @click.prevent="RouteAssistant.goToReports($route.params.accountId)">
                <a :href="reportsUrl" title="Reports" @click="activateLoading"><i class="ddi ddi-reports" /><span>Reports</span></a>
              </li>
            </ul>
          </template>
        </more-button-dropdown>
      </div>
    </div>
    <div class="right">
      <!-- GLOBAL SEARCH -->
      <div 
        v-click-outside="hide" 
        class="global-search">
        <i
          v-show="!searchActive"
          v-bind="searchIcon"
          class="ddi ddi-search"
          title="Global Search"
          @click="showSearch"
        />
        <app-header-global-search
          :search-active="searchActive"
          @toggle-search="toggleSearch"
        />
      </div>

      <div v-if="showFullOptions" class="other-options">
        <!-- NOTIFICATIONS -->
        <app-header-notification title="Notifications" />
        <!-- FRILL WIDGET LAUNCHER -->
        <app-header-frill-launcher
          v-if="$screen.isTabletOrDesktop($mq)"
          title="Feature updates & announcements" />
        <!-- GLOBAL ADD BUTTON -->
        <app-header-global-add v-if="$permissions.isNotGuest" title="Add new..." />
        <!-- PROFILE MENU -->
        <app-header-dropdown-menu />
      </div>
      <div v-if="$permissions.isImpersonating" class="app-header__impersonation" title="Impersonation Mode Active">
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke-width="2">
          <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2"/>
          <circle cx="9" cy="7" r="4"/>
          <path d="M22 21v-2a4 4 0 0 0-3-3.87"/>
          <path d="M16 3.13a4 4 0 0 1 0 7.75"/>
        </svg>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
/* General */
import { defineComponent } from 'vue'
import config from '@/config'
import * as routerNames from '@/services/router/router.names'
import logoSrc from '@/assets/img/logos/logo-donedone.svg'
import searchIconSrc from '@/assets/img/icons/search.svg'

/* Mixins */

/* Components */
import AppHeaderGlobalSearch from './components/app-header-global-search/app-header-global-search.vue'
import AppHeaderNotification from './components/app-header-notification.vue'
import AppHeaderGlobalAdd from './components/app-header-global-add.vue'
import AppHeaderDropdownMenu from './components/app-header-dropdown-menu.vue'
import AppHeaderFrillLauncher from './components/app-header-frill-launcher.vue'

/* Helpers */
import RouteAssistant from '@/helpers/route-assistant'
import { useAppStore } from '@/modules/app/app.store'

/* Data types */

/* API */

export default defineComponent({
  name: 'DdAppHeader',

  components: {
    AppHeaderGlobalSearch,
    AppHeaderNotification,
    AppHeaderGlobalAdd,
    AppHeaderDropdownMenu,
    AppHeaderFrillLauncher,
  },

  props: {
    unstickHeader: {
      type: Boolean,
      default: false,
    },
    hideMobileBackButton: {
      type: Boolean,
      default: false,
    },
  },

  emits: ['toggle-search'],

  data() {
    return {
      RouteAssistant,
      logo: {
        src: logoSrc,
        alt: 'Logo DoneDone',
      },
      logoSize: 32,
      searchIcon: {
        src: searchIconSrc,
        alt: 'Search',
      },
      searchActive: false,
      routerNames,
    }
  },

  computed: {
    accountID(): string {
      return this.$route.params.accountId
    },
    showFullOptions(): boolean {
      return (
        !['xs'].includes(this.$mq) ||
        (['xs'].includes(this.$mq) && !this.searchActive)
      )
    },
    showNav(): boolean {
      if (this.$screen.isTabletOrMobile(this.$mq)) {
        return false
      } else if (['md', 'lg'].includes(this.$mq)) {
        return !this.searchActive
      }
      return true
    },
    reportsUrl(): string {
      return `${config.serverSideAppUrl}/app/${this.accountID}/reports`
    },
    workflowsUrl(): string {
      return `${config.serverSideAppUrl}/app/${this.accountID}/workflows`
    },
  },

  methods: {
    isWorkflowsRoute(): boolean {
      return (
        this.isCurrentRoute(routerNames.WORKFLOW_HOME) ||
        this.isCurrentRoute(routerNames.WORKFLOW_DETAIL)
      )
    },
    isPeopleRoute(): boolean {
      return (
        this.isCurrentRoute(routerNames.PEOPLE) ||
        this.isCurrentRoute(routerNames.PEOPLE_CREATE) ||
        this.isCurrentRoute(routerNames.PERSON_DETAIL) ||
        this.isCurrentRoute(routerNames.COMPANY_CREATE)
      )
    },
    isGuestRoute(): boolean {
      return (
        this.isCurrentRoute(routerNames.GUESTS) ||
        this.isCurrentRoute(routerNames.GUESTS_CREATE) ||
        this.isCurrentRoute(routerNames.GUESTS_DETAIL) ||
        this.isCurrentRoute(routerNames.COMPANY_GUESTS_CREATE)
      )
    },
    isCurrentRoute(pathName: string): boolean {
      return pathName.toLowerCase() === this.$route.name
    },
    toggleSearch(val: boolean) {
      this.searchActive = val
      this.$emit('toggle-search', val)
    },
    showSearch() {
      this.toggleSearch(true)
      this.$emit('toggle-search', true)
    },
    hide() {
      if (this.searchActive) {
        this.toggleSearch(false)
      }
    },
    activateLoading() {
      useAppStore().isLoading = true
    },
  },
})
</script>

<style lang="scss">
.app-header {
  position: absolute;
  top: 0;
  right: 0;
  left: 0;
  z-index: 1050;
  display: flex;
  align-items: center;
  justify-content: space-between;
  height: $navbar-mobile-height;
  padding: 0 15px;
  background-color: var(--color-background-header);
  border-bottom: var(--border-bottom);

  > .left {
    display: flex;
    align-items: center;
    gap: 14px;

    .mobile-back-button {
      cursor: pointer;
      font-size: $dd2-size--app-header-icon;
      color: var(--icon-color);
    }

    > .nav {
      display: flex;
      align-items: center;

      > .more-nav {
        margin-left: 8px;
        margin-top: 3px;

        .dropdown__popper {
          background-color: var(--color-background-header);

          ul li:hover {
            background-color: var(--dropdown-item-hover-bg);
          }

          a,
          span,
          i {
            color: var(--color-text-primary);
          }
        }
      }

      > ul {
        display: flex;
        align-items: center;
        list-style-type: none;
        margin-bottom: 0;
        padding: 0;

        > li {
          margin-left: 10px;
          margin-right: 10px;
          margin-bottom: 4px;
          border-bottom: solid 2px var(--color-background-header);

          @media screen and (max-width: 850px) {
            margin-left: 5px;
            margin-right: 5px;
          }

          &:hover,
          &.selected {
            border-bottom: solid 2px $reddish-orange;

            > a {
              color: var(--color-text-secondary);
            }
          }

          &.selected {
            > a {
              font-weight: 500;
            }
          }

          a {
            display: flex;
            align-items: center;
            height: $navbar-desktop-height;
            padding-top: 10px;
            text-decoration: none;
            @include lettering--p;
            color: var(--color-text-primary);
          }
        }
      }
    }
  }

  > .right {
    display: flex;
    align-items: center;

    .global-search {
      outline: none;
      @include clickable-gray-color-states;

      > i {
        display: flex;
        font-size: $dd2-size--app-header-icon;
      }
    }

    .other-options {
      margin-left: 24px;
      display: flex;
      align-items: center;
      gap: 24px;

      > .app-header-dropdown-menu {
        margin-left: -4px;
      }
    }
  }

  &__fixed {
    position: fixed;
  }

  @media (min-width: $--sm) {
    height: $navbar-desktop-height;
    line-height: 62px;
  }

  &__impersonation {
    padding: 20px;

    svg {
      stroke: red;
    }
  }
}
</style>
